import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { BellRing, Loader2, Mail, MessageCircle, Settings2, Users, X } from 'lucide-react';
import { FillSlotDialog } from './FillSlotDialog';
import { toast } from 'sonner';
import { API } from '../../../lib/api';
import { waUrl } from '../../../lib/waShare';
import { formatErr, inputCls } from '../billing/shared';

const niceDay = (d) => new Date(`${d}T00:00:00`).toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'short' });
const tone = (r) => (r.held ? 'border-slate-200 bg-slate-50 text-slate-400' : r.free === 0 ? 'border-rose-200 bg-rose-50 text-rose-800' : r.free < r.cap ? 'border-amber-200 bg-amber-50 text-amber-900' : 'border-emerald-200 bg-emerald-50 text-emerald-900');

const AlertSettings = ({ data, authHeader, onSaved }) => {
  const [enabled, setEnabled] = useState(data.settings.enabled);
  const [emails, setEmails] = useState(data.settings.extra_emails.join(', '));
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/appointments/slot-alert/settings`, { enabled, extra_emails: emails.split(/[,\s]+/).filter(Boolean) }, { headers: authHeader() });
      toast.success('Slot alert settings saved'); onSaved();
    } catch (e) { toast.error(formatErr(e)); }
    setSaving(false);
  };
  return (
    <div className="mt-3 space-y-2 rounded-lg border border-slate-200 bg-white p-3" data-testid="slot-alert-settings">
      <label className="flex items-center gap-2 text-xs font-semibold text-slate-700">
        <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} data-testid="slot-alert-enabled" />
        Email this summary every morning at 8:00 (Mon–Sat)
      </label>
      <p className="text-[11px] text-slate-500">Always goes to the owner ({data.recipients[0] || 'no owner email'}). Add more addresses, separated by commas:</p>
      <input className={inputCls} placeholder="manager@workshop.com, frontdesk@workshop.com" value={emails} onChange={(e) => setEmails(e.target.value)} data-testid="slot-alert-emails" />
      <button type="button" onClick={save} disabled={saving} className="rounded-lg bg-teal-600 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50" data-testid="slot-alert-save">
        {saving ? 'Saving…' : 'Save'}
      </button>
    </div>
  );
};

export const SlotAlertCard = ({ authHeader }) => {
  const [data, setData] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [sending, setSending] = useState(false);
  const [filling, setFilling] = useState(false);
  const load = useCallback(() => axios.get(`${API}/appointments/slot-alert`, { headers: authHeader() })
    .then(({ data: d }) => setData(d)).catch(() => setData(false)), [authHeader]);
  useEffect(() => { load(); }, [load]);

  const emailNow = async () => {
    setSending(true);
    try {
      const { data: r } = await axios.post(`${API}/appointments/slot-alert/send`, {}, { headers: authHeader() });
      toast.success(`Emailed to ${r.to.join(', ')}`); load();
    } catch (e) { toast.error(formatErr(e)); }
    setSending(false);
  };

  if (data === false) return null;
  if (!data) return <div className="rounded-xl border border-slate-200 bg-white p-4"><Loader2 size={15} className="animate-spin text-slate-400" /></div>;
  return (
    <section className="rounded-xl border border-teal-200 bg-teal-50/40 p-4" data-testid="slot-alert-card">
      <div className="flex flex-wrap items-center gap-2">
        <BellRing size={16} className="text-teal-700" />
        <h2 className="text-sm font-semibold text-slate-900" data-testid="slot-alert-title">
          {data.free_total} free slot{data.free_total === 1 ? '' : 's'} for {niceDay(data.date)}
        </h2>
        {data.last_sent && <span className="text-[11px] text-slate-500" data-testid="slot-alert-last">Emailed {new Date(data.last_sent.sent_at).toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit' })}</span>}
        <span className="flex-1" />
        {data.free_total > 0 && (
          <button type="button" onClick={() => setFilling(true)} className="inline-flex items-center gap-1.5 rounded-full bg-slate-900 px-3 py-1.5 text-xs font-semibold" style={{ color: '#fff' }} data-testid="slot-alert-fill">
            <Users size={13} /> Fill a slot
          </button>
        )}
        <a href={waUrl('', data.text)} target="_blank" rel="noopener noreferrer" data-testid="slot-alert-wa"
          className="inline-flex items-center gap-1.5 rounded-full bg-emerald-600 px-3 py-1.5 text-xs font-semibold" style={{ color: '#fff' }}>
          <MessageCircle size={13} /> Share on WhatsApp
        </a>
        <button type="button" onClick={emailNow} disabled={sending} className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 disabled:opacity-50" data-testid="slot-alert-email">
          {sending ? <Loader2 size={13} className="animate-spin" /> : <Mail size={13} />} Email now
        </button>
        <button type="button" onClick={() => setShowSettings((v) => !v)} className="rounded-full p-1.5 text-slate-500 hover:bg-white" aria-label="Slot alert settings" data-testid="slot-alert-settings-toggle">
          {showSettings ? <X size={15} /> : <Settings2 size={15} />}
        </button>
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        {data.rows.map((r) => (
          <span key={r.window} className={`rounded-lg border px-2.5 py-1 text-[11px] font-semibold ${tone(r)}`} data-testid={`slot-alert-row-${r.window}`}>
            <span className="font-mono">{r.window}</span> · {r.held ? 'Held' : `${r.free} of ${r.cap} free`}
          </span>
        ))}
      </div>
      {filling && <FillSlotDialog alert={data} authHeader={authHeader} onClose={() => setFilling(false)} />}
      {showSettings && <AlertSettings data={data} authHeader={authHeader} onSaved={() => { setShowSettings(false); load(); }} />}
    </section>
  );
};
